import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as cognitoSignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { authService } from '../services/auth';

// Decode JWT payload without verification (we trust Cognito's tokens)
function decodeIdToken(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (e) {
    console.warn('[AuthContext] Failed to decode ID token:', e);
    return {};
  }
}

const AuthContext = createContext(null);

async function bridgeCognitoToLocalUser(cognitoUser, attributes) {
  const email = attributes?.email || cognitoUser?.signInDetails?.loginId || '';
  const cognitoId = cognitoUser?.userId || cognitoUser?.username || '';
  const displayName = attributes?.name || attributes?.preferred_username || email.split('@')[0] || 'User';
  const picture = attributes?.picture || null;

  // Sync current user to DynamoDB
  await authService.syncCognitoUserToDb({
    email,
    displayName,
    cognitoId,
    avatar: picture,
    avatarType: picture ? 'upload' : null,
  });

  // Hydrate localStorage with all DynamoDB users so sync reads have fresh data
  await authService.hydrateFromDb();

  let localUser = authService.getAllUsers().find(
    (u) => u.email?.toLowerCase() === email.toLowerCase()
  );

  if (!localUser) {
    try {
      localUser = authService.addUser({
        username: email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, ''),
        displayName,
        email,
        balance: 0,
      });
    } catch (e) {
      localUser = authService.addUser({
        username: (email.split('@')[0] + '_' + cognitoId.slice(-4)).toLowerCase().replace(/[^a-z0-9_]/g, ''),
        displayName,
        email,
        balance: 0,
      });
    }
  }

  if (localUser && !localUser.avatarType && picture) {
    authService.updateUser(localUser.id, { avatar: picture, avatarType: 'upload' });
    localUser = authService.getAllUsers().find((u) => u.id === localUser.id);
  }

  if (localUser && !localUser.cognitoId) {
    authService.updateUser(localUser.id, { cognitoId });
    localUser = authService.getAllUsers().find((u) => u.id === localUser.id);
  }

  // Ensure admin email always has admin role
  if (localUser && email.toLowerCase() === 'chrispalm84@gmail.com' && localUser.role !== 'admin') {
    authService.updateUser(localUser.id, { role: 'admin' });
    localUser = authService.getAllUsers().find((u) => u.id === localUser.id);
  }

  return localUser;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authCheckDone = useRef(false);

  const checkAuth = useCallback(async () => {
    console.log('[AuthContext] checkAuth called');
    try {
      const cognitoUser = await getCurrentUser();
      console.log('[AuthContext] getCurrentUser success:', cognitoUser?.userId);

      // Use fetchAuthSession to get the ID token — avoids GetUser API call
      // which requires aws.cognito.signin.user.admin scope
      const session = await fetchAuthSession();
      const idTokenStr = session?.tokens?.idToken?.toString();

      let attributes = {};
      if (idTokenStr) {
        const claims = decodeIdToken(idTokenStr);
        console.log('[AuthContext] ID token claims:', Object.keys(claims).join(', '));
        attributes = {
          email: claims.email,
          name: claims.name || claims['cognito:username'],
          preferred_username: claims.preferred_username,
          picture: claims.picture,
          sub: claims.sub,
        };
      }
      console.log('[AuthContext] attributes from ID token:', attributes.email, attributes.name);

      const localUser = await bridgeCognitoToLocalUser(cognitoUser, attributes);
      setUser(localUser);
      console.log('[AuthContext] User set:', localUser?.email);
    } catch (e) {
      console.log('[AuthContext] checkAuth failed:', e.message || e);
      setUser(null);
    }
    setLoading(false);
    authCheckDone.current = true;
  }, []);

  useEffect(() => {
    // Detect OAuth callback — URL contains ?code= from Cognito redirect
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuthCallback = urlParams.has('code') && urlParams.has('state');

    if (isOAuthCallback) {
      // We're returning from Google/Cognito OAuth redirect.
      // DON'T call checkAuth now — Amplify is still processing the code exchange.
      // Wait for the Hub 'signInWithRedirect' event which fires after tokens are stored.
      console.log('[AuthContext] OAuth callback detected — waiting for Hub event');
    } else {
      // Normal page load — check if we have an existing session
      console.log('[AuthContext] Normal load — checking existing session');
      checkAuth();
    }

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      console.log('[AuthContext] Hub auth event:', payload.event);
      switch (payload.event) {
        case 'signInWithRedirect':
          // OAuth flow complete — tokens are now stored, safe to call getCurrentUser
          console.log('[AuthContext] signInWithRedirect — checking auth now');
          await checkAuth();
          break;
        case 'signedIn':
          // Email/password sign-in complete
          if (!authCheckDone.current) {
            await checkAuth();
          }
          break;
        case 'signedOut':
          setUser(null);
          setLoading(false);
          break;
        case 'signInWithRedirect_failure':
          console.error('[AuthContext] OAuth failed:', payload.data);
          setUser(null);
          setLoading(false);
          break;
        default:
          break;
      }
    });

    // Safety timeout: if OAuth callback and no Hub event after 10s, try checkAuth anyway
    let safetyTimeout;
    if (isOAuthCallback) {
      safetyTimeout = setTimeout(() => {
        if (!authCheckDone.current) {
          console.log('[AuthContext] Safety timeout — trying checkAuth');
          checkAuth();
        }
      }, 10000);
    }

    return () => {
      unsubscribe();
      if (safetyTimeout) clearTimeout(safetyTimeout);
    };
  }, [checkAuth]);

  const signIn = async () => {
    await checkAuth();
  };

  const signOut = async () => {
    try {
      await cognitoSignOut();
    } catch (e) { /* ignore */ }
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    if (user) {
      const freshUser = authService.getAllUsers().find((u) => u.id === user.id);
      if (freshUser) setUser(freshUser);
    }
  }, [user]);

  const [originalUser, setOriginalUser] = useState(null);

  // Preview only: the UI shows the target's view, but API calls still use the signed-in
  // Cognito session, so anything saved is saved as the real user.
  const impersonate = (targetOrId) => {
    const target = typeof targetOrId === 'object'
      ? targetOrId
      : authService.getAllUsers().find((u) => u.id === targetOrId || u.cognitoId === targetOrId);
    if (!target) return;
    if (!originalUser) setOriginalUser(user);
    setUser(target);
  };

  const stopImpersonating = async () => {
    if (originalUser) {
      setUser(originalUser);
      setOriginalUser(null);
    }
  };

  const isImpersonating = !!originalUser;

  const value = {
    user,
    loading,
    signIn,
    signOut,
    refreshUser,
    impersonate,
    stopImpersonating,
    isImpersonating,
    originalUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
